import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';

export type NotificationSeverity = 'info' | 'warning' | 'critical';
export type NotificationType = 'battery' | 'forecast' | 'solar' | 'load' | 'system';

export interface NotificationEvent {
  id: string;
  property_id: string;
  severity: NotificationSeverity;
  type: NotificationType;
  title: string;
  message: string;
  context: Record<string, unknown>;
  created_at: string;
  seen_at: string | null;
  delivered_push_at: string | null;
}

interface UseNotificationsOptions {
  limit?: number;
  pollIntervalMs?: number;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { limit = 20, pollIntervalMs = 60000 } = options;
  const { selectedPropertyId } = useApp();
  const queryClient = useQueryClient();

  // Fetch notifications for the selected property.
  // Customer feed: only events actually surfaced to a real delivery surface —
  // delivered ('sent'), a resolution ('resolved'), or explicitly surfaced in-app
  // (in_app_delivered_at). Excludes 'evaluated'/'ineligible'/'delivery_failed'/
  // 'suppressed' (audit-only). A bare row insert is NOT in-app delivery.
  const { data: notifications = [], isLoading, error, refetch } = useQuery({
    queryKey: ['notifications', selectedPropertyId, limit],
    queryFn: async () => {
      if (!selectedPropertyId) return [];

      // Use type assertion to handle the enum types
      const { data, error } = await supabase
        .from('notification_events')
        .select('*')
        .eq('property_id', selectedPropertyId)
        .or('outcome.in.(sent,resolved),in_app_delivered_at.not.is.null')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      
      // Cast the data to our expected type
      return (data || []) as unknown as NotificationEvent[];
    },
    enabled: !!selectedPropertyId,
    refetchInterval: pollIntervalMs,
    staleTime: 30000, // Consider data stale after 30s
  });

  // Mark notification as seen
  const markSeenMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notification_events')
        .update({ seen_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', selectedPropertyId] });
    },
  });

  // Mark all as seen
  const markAllSeenMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPropertyId) return;

      const { error } = await supabase
        .from('notification_events')
        .update({ seen_at: new Date().toISOString() })
        .eq('property_id', selectedPropertyId)
        .is('seen_at', null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', selectedPropertyId] });
    },
  });

  // Computed values
  const unseenNotifications = notifications.filter(n => !n.seen_at);
  const unseenCriticalCount = unseenNotifications.filter(n => n.severity === 'critical').length;
  const unseenWarningCount = unseenNotifications.filter(n => n.severity === 'warning').length;

  // Highest unseen severity (for bell indicator)
  const highestUnseenSeverity: NotificationSeverity | null = 
    unseenCriticalCount > 0 ? 'critical' :
    unseenWarningCount > 0 ? 'warning' : null;

  return {
    notifications,
    isLoading,
    error,
    refetch,
    markSeen: markSeenMutation.mutate,
    markAllSeen: markAllSeenMutation.mutate,
    isMarkingAllSeen: markAllSeenMutation.isPending,
    unseenCount: unseenNotifications.length,
    unseenCriticalCount,
    unseenWarningCount,
    highestUnseenSeverity,
  };
}
